import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, Trash2 } from 'lucide-react';
import { seedApi } from '@/lib/seedApi';
import { useToast, useConfirm } from '@/components/ui';

/**
 * Sample-data banner (NUMBER_ONE_PLAN W2.3). The first-contact seed gives a
 * brand-new workspace something to look at; this banner makes that explicit
 * and reversible: it tells the user the data is a demo and offers a one-click
 * "Clear sample data" purge. For a workspace that has no sample data AND an
 * empty feed (legacy or already-cleared), it instead offers a one-click
 * "Load sample data" so the empty state is never a dead end.
 */
const TOUCHED_KEYS = [
  'signals',
  'deals',
  'accounts',
  'companies',
  'contacts',
  'people',
  'inbox-threads',
  'approvals',
  'seed-status',
];

export function SampleDataBanner({ wsId, feedEmpty }: { wsId: string; feedEmpty: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const status = useQuery({
    queryKey: ['seed-status', wsId],
    queryFn: () => seedApi.status(wsId),
    enabled: !!wsId,
  });

  const invalidateAll = () => {
    for (const k of TOUCHED_KEYS) qc.invalidateQueries({ queryKey: [k, wsId] });
    // The feed signal list is keyed without the trailing wsId segment guard.
    qc.invalidateQueries({ queryKey: ['signals', wsId] });
  };

  const clear = useMutation({
    mutationFn: () => seedApi.clear(wsId),
    onSuccess: () => {
      invalidateAll();
      toast.success(t('seed.cleared_toast'));
    },
    onError: () => toast.error(t('seed.clear_failed')),
  });

  const load = useMutation({
    mutationFn: () => seedApi.load(wsId),
    onSuccess: () => {
      invalidateAll();
      toast.success(t('seed.loaded_toast'));
    },
    onError: () => toast.error(t('seed.load_failed')),
  });

  async function askClear() {
    const ok = await confirm({
      title: t('seed.confirm_clear_title'),
      description: t('seed.confirm_clear_body'),
      confirmLabel: t('seed.confirm_clear_confirm'),
      variant: 'danger',
    });
    if (ok) clear.mutate();
  }

  if (status.isLoading || !status.data) return null;

  // Sample data present → offer to clear it.
  if (status.data.present) {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5">
        <Sparkles className="h-4 w-4 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-amber-900">{t('seed.banner_title')}</p>
          <p className="text-xs text-amber-700">
            {t('seed.banner_sub', { count: status.data.total })}
          </p>
        </div>
        <button
          onClick={askClear}
          disabled={clear.isPending}
          className="inline-flex items-center gap-1.5 shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {clear.isPending ? t('seed.clearing') : t('seed.clear_button')}
        </button>
      </div>
    );
  }

  // No sample data and an empty feed → offer to load it (never a dead end).
  if (feedEmpty) {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-accent-200 bg-accent-50/60 px-4 py-3">
        <Sparkles className="h-4 w-4 shrink-0 text-accent-600" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-ink-900">{t('seed.load_title')}</p>
          <p className="text-xs text-ink-600">{t('seed.load_sub')}</p>
        </div>
        <button
          onClick={() => load.mutate()}
          disabled={load.isPending}
          className="inline-flex items-center gap-1.5 shrink-0 rounded-md bg-accent-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-accent-700 disabled:opacity-60"
        >
          {load.isPending ? t('seed.loading') : t('seed.load_button')}
        </button>
      </div>
    );
  }

  return null;
}
